import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    if (!dialog) {
      // Inline instance (header field). Three behaviours the dialog used to
      // provide have to come from somewhere else, or they are silently lost:
      //
      //   empty state - normally also loaded by DialogOpenEvent. The
      //     requestIdleCallback below already covers the common case, but only
      //     if RecentlyViewed is populated at connect time. Focus is the
      //     honest inline equivalent of "the user opened search".
      //   reset       - DialogCloseEvent used to clear results. Inline there is
      //     no close, so results would persist forever. Chosen trigger:
      //     focus leaving the component entirely. Escape already resets via
      //     onSearchKeyDown, and clicking a result navigates away, so this
      //     only covers the tab-away / click-elsewhere case.
      //   CMD+K       - see #handleKeyboardShortcut.
      this.refs.searchInput.addEventListener(
        'focus',
        () => {
          this.#inlineOpen = true;
          this.#loadEmptyState();
          this.#syncExpandedState();
        },
        { signal }
      );
      this.addEventListener('focusout', this.#handleInlineFocusOut, { signal });
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }

    this.#syncExpandedState();
  }

  /**
   * Inline only: reset once focus has genuinely left the component.
   * relatedTarget is null when focus goes to the page chrome or another
   * window; both count as leaving.
   * @param {FocusEvent} event
   */
  #handleInlineFocusOut = (event) => {
    const next = /** @type {Node | null} */ (event.relatedTarget);
    if (next && this.contains(next)) return;
    this.#inlineOpen = false;
    this.#resetSearch();
    this.#syncExpandedState();
  };

  /**
   * Inline only: whether the field is engaged. The panel must be closed at
   * rest -- the empty state is server-rendered into the markup with class
   * `predictive-search-dropdown` already applied, so "is there a dropdown in
   * the DOM" is NOT the same question as "should the panel be showing", and
   * using the former leaves recently-viewed products hanging under the header
   * on every page load.
   * @type {boolean}
   */
  #inlineOpen = false;

  /**
   * Keep the combobox's aria-expanded honest, and re-id the injected results
   * so two instances on one page do not both claim
   * id="predictive-search-results".
   *
   * Neither of these existed before. aria-expanded was hardcoded "false" in
   * markup and never written; aria-activedescendant was never set at all, so a
   * screen reader could not follow the highlight that #currentIndex moves.
   * That mattered little in a modal, where the whole surface is search. It
   * matters inline, where the field sits among other header controls.
   */
  #syncExpandedState = () => {
    const { searchInput, predictiveSearchResults } = this.refs;
    if (!searchInput || !predictiveSearchResults) return;

    const instanceId = this.dataset.instanceId || 'predictive-search';
    const resultsId = `${instanceId}-results`;

    // The results markup arrives from the shared section with a fixed id.
    const injected = predictiveSearchResults.querySelector('#predictive-search-results, [role="listbox"]');
    if (injected) injected.id = resultsId;

    searchInput.setAttribute('aria-owns', resultsId);
    searchInput.setAttribute('aria-controls', resultsId);

    const engaged = this.dialog ? true : this.#inlineOpen;

    // "Has results" has to mean "has results for a query". #allResultsItems is a
    // live DOM getter, and the empty state that snippets/predictive-search-empty-state.liquid
    // renders into this component on load is made of the same
    // .predictive-search-results__list markup as a real result set -- measured: 8
    // items (recently viewed + products) present before anything is typed. So
    // `items > 0` was already true at rest, and the panel opened as soon as the
    // field took focus, showing recently-viewed products nobody asked for.
    //
    // Requiring a non-empty query is what separates the two, because the empty
    // state is only ever rendered while the input is empty (#resetSearch clears
    // the value and then renders it; #search routes an empty term straight back
    // to #resetSearch). Counting items alone cannot tell them apart.
    //
    // Only snippets/nysta-header-search.liquid keys off this attribute, so this
    // changes the inline panel and nothing else. It also stops the modal's input
    // claiming aria-expanded="true" while its dialog is shut.
    const hasQuery = (searchInput.value ?? '').trim().length > 0;
    const open = engaged && hasQuery && Boolean(injected) && (this.#allResultsItems?.length ?? 0) > 0;
    searchInput.setAttribute('aria-expanded', open ? 'true' : 'false');

    if (!open) searchInput.removeAttribute('aria-activedescendant');
  };

  /**
   * Point aria-activedescendant at the highlighted option.
   * @param {Element | null} item
   */
  #syncActiveDescendant = (item) => {
    const { searchInput } = this.refs;
    if (!searchInput) return;
    if (!item) {
      searchInput.removeAttribute('aria-activedescendant');
      return;
    }
    if (!item.id) {
      const instanceId = this.dataset.instanceId || 'predictive-search';
      item.id = `${instanceId}-option-${this.#allResultsItems?.indexOf(item) ?? 0}`;
    }
    searchInput.setAttribute('aria-activedescendant', item.id);
  };

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      // Inline there is no dialog to toggle, and `this.dialog?.toggleDialog()`
      // alone would leave the shortcut silently doing nothing. Focus the field
      // instead -- same intent, no dialog required.
      if (this.dialog) {
        this.dialog.toggleDialog();
      } else {
        event.preventDefault();
        this.refs.searchInput?.focus();
      }
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) {
      this.#syncExpandedState();
      return;
    }
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.#syncActiveDescendant(activeItem);

    // Only re-assert focus when an item is actually highlighted.
    //
    // This focus() exists for arrow-key navigation: the highlight moves through
    // the results while the caret has to stay in the input. It used to run
    // unconditionally, including for `#currentIndex = -1`, which is the FIRST
    // statement of #resetSearch -- so every reset yanked focus back into the
    // field. Measured stack, captured on a real click outside the field:
    //
    //   focus() on input.search-input
    //     <- set #currentIndex  <- #resetSearch  <- #handleInlineFocusOut
    //
    // The symptoms that came from it: clicking away left the caret in the field
    // (focus did leave -- blur and focusout both fired and activeElement became
    // body -- and was then pulled straight back), clicking back in fired no new
    // focus event so the panel never reopened, and the field held focus on page
    // load because connectedCallback -> #loadEmptyState -> resetSearch(false)
    // reaches the same setter.
    //
    // `activeItem` is null exactly when index is -1, so this distinguishes
    // "move the highlight" from "tear the highlight down" without a new flag.
    if (activeItem) this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      // #resetSearch clears the value and re-renders the empty state, but it does
      // not touch aria-expanded. Measured before this line existed: after Escape
      // the inline field held value="" and aria-expanded="true", so the panel
      // stayed open with the highlight cleared -- a combobox claiming an expanded
      // listbox for a query it no longer has. Syncing here is what actually closes
      // the inline panel, because snippets/nysta-header-search.liquid keys its
      // visibility off that attribute.
      //
      // Both instances share this handler. In the modal the effect is limited to
      // the attribute: nothing keys off the modal input's aria-expanded, and the
      // dialog's own Escape-to-close is untouched, since this branch neither
      // prevents default nor stops propagation.
      //
      // Called back-to-back rather than awaited, matching #handleInlineFocusOut.
      // #resetSearch clears searchInput.value synchronously before its first
      // await, so the sync below already sees the empty query.
      this.#resetSearch();
      this.#syncExpandedState();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#syncExpandedState();
        this.#resetScrollPositions();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
